import { Dayjs } from "dayjs"
import { US_DATE_FORMAT_WITHOUT_YEAR, US_DATE_FORMAT_WITH_YEAR } from "config"
import {
  IMealTypePercent,
  IMealTypePercentItem,
  IWaterWeeklyChartDataItem,
  IWeeklyChartDataItem,
  IWeeklyOverview,
  IWeeklyOverviewResponse,
} from "./types"

const roundPercentsToTotal100 = (
  items: Omit<IMealTypePercentItem, "percent">[],
  total: number,
): IMealTypePercent => {
  if (total <= 0) {
    return items.map((item) => ({ ...item, percent: 0 }))
  }

  const rounded = items.map((item) => {
    const raw = (item.value / total) * 100
    return {
      ...item,
      percent: Math.floor(raw),
      remainder: raw % 1,
    }
  })

  const sum = rounded.reduce((acc, x) => acc + x.percent, 0)
  const deficit = 100 - sum

  rounded
    .slice()
    .sort((a, b) => b.remainder - a.remainder)
    .forEach((item, i) => {
      if (i < deficit) {
        item.percent += 1
      }
    })

  return rounded.map(({ remainder: _, ...item }) => item)
}

export const computeDayMealTypePercent = (
  item: IWeeklyChartDataItem,
): IMealTypePercent => {
  const { breakfast, lunch, dinner, snack, other } = item
  const total = breakfast + lunch + dinner + snack + other

  return roundPercentsToTotal100(
    [
      { name: "Breakfast", value: breakfast, color: "#2D96FF" },
      { name: "Lunch", value: lunch, color: "#FF7A1A" },
      { name: "Dinner", value: dinner, color: "#8948FA" },
      { name: "Snacks", value: snack, color: "#F7BA1E" },
      { name: "Other", value: other, color: "#667085" },
    ],
    total,
  )
}

export const getEmptyFoodChartData = (date: Dayjs): IWeeklyChartDataItem[] => {
  const weekStart = date.startOf("week")
  return Array.from({ length: 7 }, (_, i) => {
    const day = weekStart.add(i, "day")
    return {
      name: day.format(US_DATE_FORMAT_WITHOUT_YEAR),
      date: day.format(US_DATE_FORMAT_WITH_YEAR),
      breakfast: 0,
      lunch: 0,
      dinner: 0,
      snack: 0,
      other: 0,
      goalCalories: 0,
    }
  })
}

export const getEmptyWaterChartData = (
  date: Dayjs,
): IWaterWeeklyChartDataItem[] => {
  const weekStart = date.startOf("week")
  return Array.from({ length: 7 }, (_, i) => {
    const day = weekStart.add(i, "day")
    return {
      name: day.format(US_DATE_FORMAT_WITHOUT_YEAR),
      date: day.format(US_DATE_FORMAT_WITH_YEAR),
      amount: 0,
    }
  })
}

export const prepareWeeklyOverviewData = (
  weeklyOverview: IWeeklyOverviewResponse,
): IWeeklyOverview => {
  const {
    weeklyChartData,
    mostCalories,
    mostCarbs,
    mostProtein,
    mostFat,
    gainedCalories,
  } = weeklyOverview

  const breakfast = weeklyChartData.reduce((acc, x) => acc + x.breakfast, 0)
  const lunch = weeklyChartData.reduce((acc, x) => acc + x.lunch, 0)
  const dinner = weeklyChartData.reduce((acc, x) => acc + x.dinner, 0)
  const snack = weeklyChartData.reduce((acc, x) => acc + x.snack, 0)
  const other = weeklyChartData.reduce((acc, x) => acc + x.other, 0)

  const mealTypePercent = roundPercentsToTotal100(
    [
      { name: "Breakfast", value: breakfast, color: "#2D96FF" },
      { name: "Lunch", value: lunch, color: "#FF7A1A" },
      { name: "Dinner", value: dinner, color: "#8948FA" },
      { name: "Snacks", value: snack, color: "#F7BA1E" },
      { name: "Other", value: other, color: "#667085" },
    ],
    gainedCalories,
  )

  return {
    mealTypePercent,
    gainedCalories,
    mostMeals: [
      {
        title: "Most Calories",
        meals: mostCalories,
        nutritionType: "calories",
      },
      {
        title: "Most Protein",
        meals: mostProtein,
        nutritionType: "protein",
      },
      {
        title: "Most Carbs",
        meals: mostCarbs,
        nutritionType: "carbs",
      },
      {
        title: "Most Fat",
        meals: mostFat,
        nutritionType: "fat",
      },
    ],
    weeklyChartData,
  }
}
